import { useState, useEffect, useRef } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { trpc } from '@org/clients/school';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  Skeleton,
  FeedbackView,
  parseFeedbackContent,
  seedAbilityChecks,
} from '@org/ui';
import type { FeedbackContent } from '@org/ui';
import { useTeacherId } from '../../../../components/teacher-picker';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/students/$studentId/feedback/$feedbackId')({
  component: FeedbackDetailPage,
});

const STATUS_CLASS_NAMES: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-600',
  generating: 'bg-yellow-100 text-yellow-700 animate-pulse',
  generated: 'bg-blue-100 text-blue-700',
  published: 'bg-emerald-100 text-emerald-700',
};

function formatDate(date: Date | string | null): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('pt-BR');
}

function FeedbackDetailPage() {
  const { t } = useTranslation('admin');
  const { studentId, feedbackId } = Route.useParams();
  const teacherId = useTeacherId();
  const [editingContent, setEditingContent] = useState<FeedbackContent | null>(null);
  // Serialized snapshot of what is currently persisted on the server. Used to
  // detect unsaved changes for autosave (and to avoid re-saving the unchanged
  // seeded grid on open).
  const savedSerializedRef = useRef<string | null>(null);

  const feedbackQuery = trpc.teacher.feedback.get.useQuery(
    { feedbackId, teacherId },
    {
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        return status === 'pending' || status === 'generating' ? 3000 : false;
      },
    },
  );

  const studentProfile = trpc.teacher.students.profile.useQuery({ teacherId, studentId });

  const levelId = studentProfile.data?.student.levelId ?? null;

  // Ability catalog for the student's level — used to seed the end-of-term
  // Ability Check grid on final feedback. Skipped when there is no level.
  const abilitiesQuery = trpc.abilities.listByLevel.useQuery(
    { levelId: levelId ?? '' },
    { enabled: !!levelId },
  );

  const status = feedbackQuery.data?.status;
  // Only the teacher who wrote the report (or an admin acting as her) edits it; others read.
  const isOwn = feedbackQuery.data?.isOwn ?? true;
  const canEdit = (feedbackQuery.data?.canEdit ?? false) && (status === 'generated' || status === 'published');

  // The classes of the report's period, as they stand now: the attendance and
  // homework dots. Each dot opens the teacher's view of that class for the student.
  const sessionsQuery = trpc.teacher.feedback.sessions.useQuery(
    { feedbackId, teacherId },
    { enabled: Boolean(feedbackQuery.data), staleTime: 60_000 },
  );

  // Deliberately no global refetch on update: autosave fires while the teacher
  // is typing, and refetching would reset editingContent mid-edit. Publish
  // refetches instead, to pick up the new status.
  const updateMutation = trpc.teacher.feedback.update.useMutation();

  const publishMutation = trpc.teacher.feedback.publish.useMutation({
    onSuccess: () => {
      feedbackQuery.refetch();
    },
  });

  // Build the editing content whenever the feedback or the ability catalog
  // changes. Parsing and Ability-Check seeding are computed together so the
  // seed applies no matter which input arrives last — notably when content
  // lands after a generation finishes while the catalog is already loaded
  // (otherwise the grid only appeared after a manual refresh).
  useEffect(() => {
    if (!feedbackQuery.data) return;
    const parsed = parseFeedbackContent(
      feedbackQuery.data.editedContent ?? feedbackQuery.data.generatedContent,
    );
    const next = seedAbilityChecks(parsed, feedbackQuery.data.type, abilitiesQuery.data);
    setEditingContent(next);
    // Freshly loaded/seeded content is the saved baseline; only later user edits
    // count as unsaved (the seed itself persists on the first real save/publish).
    savedSerializedRef.current = next ? JSON.stringify(next) : null;
  }, [
    feedbackQuery.data?.generatedContent,
    feedbackQuery.data?.editedContent,
    feedbackQuery.data?.type,
    abilitiesQuery.data,
  ]);

  // Autosave: debounce-persist edits while the teacher types. No-op until the
  // content is editable and actually differs from what is already saved.
  useEffect(() => {
    if (!canEdit || !editingContent) return;
    const serialized = JSON.stringify(editingContent);
    if (serialized === savedSerializedRef.current) return;
    const handle = setTimeout(() => {
      updateMutation.mutate(
        { feedbackId, teacherId, editedContent: serialized },
        { onSuccess: () => { savedSerializedRef.current = serialized; } },
      );
    }, 800);
    return () => clearTimeout(handle);
    // updateMutation.mutate / feedbackId are stable; re-running only on edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingContent, canEdit]);

  // Publish/republish flushes the latest edits first, so the published content
  // can never lag behind what the teacher currently sees.
  const handlePublish = async () => {
    if (editingContent) {
      const serialized = JSON.stringify(editingContent);
      if (serialized !== savedSerializedRef.current) {
        await updateMutation.mutateAsync({ feedbackId, teacherId, editedContent: serialized });
        savedSerializedRef.current = serialized;
      }
    }
    publishMutation.mutate({ feedbackId, teacherId });
  };

  const isDirty =
    editingContent != null && JSON.stringify(editingContent) !== savedSerializedRef.current;
  const saveStatusLabel = updateMutation.isPending
    ? t('common.status.saving')
    : isDirty
      ? t('teacher.feedbackDetail.autoUnsaved')
      : t('teacher.feedbackDetail.autoSaved');

  const studentName = studentProfile.data
    ? `${studentProfile.data.student.firstName ?? ''} ${studentProfile.data.student.lastName ?? ''}`.trim() || t('teacher.studentProfile.studentFallback')
    : t('teacher.studentProfile.studentFallback');

  if (feedbackQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const feedback = feedbackQuery.data;
  if (!feedback) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" asChild className="px-0 text-sm text-[#cf2e2e]">
          <Link to="/students/$studentId/feedback" params={{ studentId }}>
            &#8592; {t('teacher.feedbackDetail.back')}
          </Link>
        </Button>
        <p className="text-sm text-gray-500">{t('teacher.feedbackDetail.notFound')}</p>
      </div>
    );
  }

  const statusKey = feedback.status as 'pending' | 'generating' | 'generated' | 'published';
  const badgeClassName = STATUS_CLASS_NAMES[statusKey] ?? STATUS_CLASS_NAMES.pending;
  const isProcessing = feedback.status === 'pending' || feedback.status === 'generating';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" asChild className="px-0 text-sm text-[#cf2e2e]">
          <Link to="/students/$studentId/feedback" params={{ studentId }}>
            &#8592; {t('teacher.feedbackDetail.backToFeedbacks')}
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-[#cf2e2e]">{t('teacher.feedbackDetail.eyebrow')}</p>
          <h1 className="text-3xl font-semibold text-gray-900">{studentName}</h1>
          <p className="text-sm text-gray-600">
            {t('teacher.feedbackDetail.period', { from: formatDate(feedback.dateFrom), to: formatDate(feedback.dateTo) })}
          </p>
          <p className="text-sm text-gray-500">{t('teacher.feedbackDetail.byTeacher', { name: feedback.teacherName })}</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={`text-xs ${badgeClassName}`}>
            {t(`teacher.feedbackDashboard.statusLabels.${statusKey}`, t('teacher.feedbackDashboard.statusLabels.pending'))}
          </Badge>
          <Badge className="bg-gray-100 text-xs text-gray-500">
            {feedback.type === 'midterm' ? t('teacher.feedbackDetail.typeMidterm') : t('teacher.feedbackDetail.typeFinal')}
          </Badge>
        </div>
      </div>

      {isProcessing && (
        <Card className="border border-yellow-200 bg-yellow-50">
          <CardContent className="flex items-center gap-3 py-6">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-yellow-500 border-t-transparent" />
            <p className="text-sm text-yellow-700">
              {feedback.status === 'pending'
                ? t('teacher.feedbackDetail.processingQueued')
                : t('teacher.feedbackDetail.processingGenerating')}
            </p>
          </CardContent>
        </Card>
      )}

      {!isProcessing && !editingContent && (
        <Card className="border border-gray-200">
          <CardContent className="py-6">
            <p className="text-sm text-gray-500">{t('teacher.feedbackDetail.contentUnavailable')}</p>
            {feedback.generatedContent && (
              <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-2 text-xs text-gray-600">
                {feedback.generatedContent}
              </pre>
            )}
          </CardContent>
        </Card>
      )}

      {!isProcessing && !isOwn && (
        <Card className="border border-sky-200 bg-sky-50/60">
          <CardHeader className="py-3">
            <CardDescription className="text-sky-900">{t('teacher.feedbackDetail.readOnly')}</CardDescription>
          </CardHeader>
        </Card>
      )}

      {!isProcessing && editingContent && (
        <>
          <FeedbackView
            content={editingContent}
            editable={canEdit}
            onContentChange={setEditingContent}
            levelName={studentProfile.data?.student.levelName ?? null}
            sessions={sessionsQuery.data?.sessions}
            classHref={(session) => (session.classId ? `/classes/${session.classId}/student/${studentId}` : null)}
          />

          {/* Action Buttons */}
          {canEdit && (
            <div className="flex items-center gap-3">
              <Button
                onClick={handlePublish}
                disabled={publishMutation.isPending || updateMutation.isPending}
                className="bg-[#cf2e2e] text-white hover:bg-[#b02525]"
              >
                {publishMutation.isPending
                  ? t('common.status.publishing')
                  : feedback.status === 'published'
                    ? t('teacher.feedbackDetail.republish')
                    : t('teacher.feedbackDetail.publish')}
              </Button>
              <span className="text-sm text-gray-400">{saveStatusLabel}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
